import React, {useCallback, useEffect, useRef, useState} from 'react';
import {useIntl} from 'react-intl';
import {Button, Checkbox, ComboBox, EMPHASIS, Heading, Pill, PillVariant, SubHeading, Text} from '@forecast-it/design-system';

import DirectApi from '../../../../directApi';
import {ConfigPageCard} from '../xero/XeroConfigPageStyled';
import {
	ConfigImportActions,
	ConfigImportActionsEnd,
	ConfigImportDescription,
	ConfigImportMessage,
	ConfigImportOption,
	ConfigImportOrganisationField,
	ConfigImportRow,
	ConfigImportRows,
	ConfigImportStatusText,
} from './QuickbooksConfigurationImportStyled';
import {loadQuickbooksCompanies} from './loadQuickbooksCompanies';
import {NOTHING_TO_IMPORT, readImportOutcome} from './qboConfigImport';

/**
 * The three configuration types (T4-179 AC3). The configGroup values are a contract with the
 * imports that write them (T4-181, T4-182, T4-183): a mismatch reads "Never imported" for ever.
 * importPath and emptyMessageId are the contract with the imports (T4-181, T4-182, T4-183), spec 032.
 */
export const CONFIG_TYPES = [
	{
		key: 'ACCOUNTS_AND_TAX',
		configGroup: 'QBO_ACCOUNTS_AND_TAX',
		labelId: 'settings.app_catalog.quickbooks.config_accounts_and_tax',
		importPath: 'quickbooks/configuration/import/accounts-and-tax',
		emptyMessageId: 'settings.app_catalog.quickbooks.config_error_no_accounts_or_tax',
	},
	{
		key: 'PRODUCTS_AND_SERVICES',
		configGroup: 'QBO_PRODUCTS_AND_SERVICES',
		labelId: 'settings.app_catalog.quickbooks.config_products_and_services',
		importPath: 'quickbooks/configuration/import/products-and-services',
		emptyMessageId: 'settings.app_catalog.quickbooks.config_error_no_products',
	},
	{
		key: 'CLASSES',
		configGroup: 'QBO_CLASSES',
		labelId: 'settings.app_catalog.quickbooks.config_classes',
		importPath: 'quickbooks/configuration/import/classes',
		emptyMessageId: 'settings.app_catalog.quickbooks.config_error_no_classes',
	},
];

export const IMPORT_STATUS_PATH = 'quickbooks/configuration/import-status';

// Both sources can hand over something that is not a list: the page stores a failed call's error
// body ({status, message, correlationId}) as its list. Anything but an array is no organisations.
const asList = value => (Array.isArray(value) ? value : []);

const organisationLabel = org => org.name || org.realmId;

const ORGANISATION_LABEL_ID = 'qbo-config-organization-label';
const statusIdFor = configType => `qbo-config-${configType.key}-status`;

/**
 * Import QuickBooks Configuration (T4-179, spec 030). Reads the status and runs the imports (spec 032).
 *
 * multiTenant - the page's quickbooks_multi_tenant flag: with 2+ organisations it shows the dropdown.
 * organisations / organisationsLoading - the list the page has already fetched. When passed, this
 *   section never repeats that call (spec 030 D4); the page passes it whenever the section shows
 *   (spec 048). When not passed, the section fetches it itself, once.
 *
 * Laid out as XeroConfigurationImport (T4-205, spec 048): only the look changed; the logic did not.
 */
const QuickbooksConfigurationImport = ({multiTenant = false, organisations, organisationsLoading = false}) => {
	const {formatMessage, formatDate, formatTime} = useIntl();

	const [fetchedOrganisations, setFetchedOrganisations] = useState([]);
	const [chosenRealmId, setChosenRealmId] = useState(null);
	// null = the status has not answered yet. Until it does, the rows show no status text: "Never imported"
	// is a claim, and we do not know yet (spec R5).
	const [statusRows, setStatusRows] = useState(null);
	const [statusUnavailable, setStatusUnavailable] = useState(false);
	const [selectedKeys, setSelectedKeys] = useState([]);

	const [importing, setImporting] = useState(false);
	// The last run's result per option, keyed by the realm it ran for (spec 032, State).
	const [outcomes, setOutcomes] = useState([]);
	// A ref, not state: a double click lands both clicks before React re-renders the disabled button.
	const runningRef = useRef(false);
	const mountedRef = useRef(true);
	useEffect(
		() => () => {
			mountedRef.current = false;
		},
		[]
	);

	// Numbers each status read. Only the latest one may set state, so a slow earlier read cannot
	// overwrite the fresher answer that came after it.
	const statusRequestRef = useRef(0);

	const loadStatus = useCallback(() => {
		statusRequestRef.current += 1;
		const request = statusRequestRef.current;
		const isCurrent = () => mountedRef.current && statusRequestRef.current === request;
		return DirectApi.Fetch_Raw(IMPORT_STATUS_PATH)
			.then(result => (result.status === 200 ? result.json() : null))
			.then(rows => {
				if (!isCurrent()) {
					return;
				}
				// Only a list is an answer. A failed read, or a body we do not recognise, must not
				// fall through to "Never imported" (spec 030 R5).
				if (Array.isArray(rows)) {
					setStatusRows(rows);
					setStatusUnavailable(false);
				} else {
					setStatusUnavailable(true);
				}
			})
			.catch(() => {
				if (isCurrent()) {
					setStatusUnavailable(true);
				}
			});
	}, []);

	useEffect(() => {
		loadStatus();
	}, [loadStatus]);

	const pageSuppliesList = organisations !== undefined;

	useEffect(() => {
		if (pageSuppliesList) {
			return;
		}
		let active = true;
		loadQuickbooksCompanies(path => DirectApi.Fetch_Raw(path)).then(list => {
			if (active) {
				setFetchedOrganisations(list);
			}
		});
		return () => {
			active = false;
		};
	}, [pageSuppliesList]);

	if (pageSuppliesList && organisationsLoading) {
		return null;
	}

	const organisationList = asList(pageSuppliesList ? organisations : fetchedOrganisations);
	// Nothing to import into. The page already offers Add directly above this section.
	if (organisationList.length === 0) {
		return null;
	}

	// Derived on every render rather than set in an effect: the first organisation is the
	// selection from the first render that has a list - including a list that arrives as a prop
	// after mount - and again whenever the chosen one is no longer in the list. The first one that
	// is reachable wins, since an inaccessible one cannot import (spec 048 D12); an organisation the
	// admin picks is kept even when it is inaccessible.
	const activeOrganisation =
		organisationList.find(org => org.realmId === chosenRealmId) ||
		organisationList.find(org => !org.inaccessible) ||
		organisationList[0];
	const activeRealmId = activeOrganisation.realmId;

	// An organisation Forecast can no longer reach is the one the list above offers Reconnect for. Its
	// import cannot succeed without a token, so the controls are disabled instead.
	const reconnectRequired = Boolean(activeOrganisation.inaccessible);
	const controlsDisabled = importing || reconnectRequired;

	const outcomeFor = configType =>
		outcomes.find(outcome => outcome.realmId === activeRealmId && outcome.key === configType.key);

	/**
	 * What a row says, highest first (spec 032, What a row shows). This run's own outcome outranks the
	 * stored row: an import that failed before writing a status row would otherwise leave an older
	 * success on screen, and a success must still show when the status re-read after it fails.
	 */
	const describe = configType => {
		const outcome = outcomeFor(configType);
		if (outcome) {
			return outcome.status === 'SUCCESS'
				? {text: formatMessage({id: 'settings.app_catalog.quickbooks.config_imported_just_now'}), tone: 'success'}
				: {text: formatMessage({id: 'settings.app_catalog.quickbooks.config_import_failed'}), tone: 'error'};
		}
		if (statusUnavailable) {
			return {text: formatMessage({id: 'settings.app_catalog.quickbooks.config_status_unavailable'}), tone: 'neutral'};
		}
		if (statusRows === null) {
			return {text: '', tone: 'neutral'};
		}
		const row = statusRows.find(
			candidate => candidate.configGroup === configType.configGroup && candidate.organisationExternalId === activeRealmId
		);
		if (!row) {
			return {text: formatMessage({id: 'settings.app_catalog.quickbooks.config_never_imported'}), tone: 'neutral'};
		}
		if (row.status !== 'SUCCESS') {
			// Red, but no message: the stored message is technical, and the row carries no reason code.
			return {text: formatMessage({id: 'settings.app_catalog.quickbooks.config_import_failed'}), tone: 'error'};
		}
		const when = row.lastImportedAt ? new Date(row.lastImportedAt) : null;
		if (!when || Number.isNaN(when.getTime())) {
			// A success we cannot date is not knowledge we can show.
			return {text: formatMessage({id: 'settings.app_catalog.quickbooks.config_status_unavailable'}), tone: 'neutral'};
		}
		return {
			text: formatMessage(
				{id: 'settings.app_catalog.quickbooks.config_last_imported'},
				{
					// react-intl 3.x drops dateStyle from its options whitelist without erroring, so
					// the parts are named explicitly - the same format as the Xero page.
					timestamp: `${formatDate(when, {day: 'numeric', month: 'short', year: 'numeric'})}, ${formatTime(when)}`,
				}
			),
			tone: 'neutral',
		};
	};

	// Only the three empty cases have copy; any other failure shows "Import failed" alone (spec 032).
	const messageFor = configType => {
		const outcome = outcomeFor(configType);
		return outcome && outcome.status === 'FAILED' && outcome.reason === NOTHING_TO_IMPORT
			? formatMessage({id: configType.emptyMessageId})
			: null;
	};

	const showImportedPill = outcomes.some(outcome => outcome.realmId === activeRealmId && outcome.status === 'SUCCESS');

	// A new selection starts a new attempt: the success feedback goes (it described the last selection),
	// and the option just changed loses its error. Other options keep theirs.
	const toggle = key => {
		setSelectedKeys(current => (current.includes(key) ? current.filter(k => k !== key) : [...current, key]));
		setOutcomes(current => current.filter(outcome => outcome.status !== 'SUCCESS' && outcome.key !== key));
	};

	// A new organisation starts clean. Done in the handler rather than an effect on the realm, so the
	// switch and the clear land in the same render.
	const chooseOrganisation = realmId => {
		setChosenRealmId(realmId);
		setSelectedKeys([]);
		setOutcomes([]);
	};

	const runImport = async () => {
		const chosen = CONFIG_TYPES.filter(type => selectedKeys.includes(type.key));
		if (runningRef.current || reconnectRequired || chosen.length === 0) {
			return;
		}
		runningRef.current = true;
		// The realm chosen now: the dropdown is locked for the run, and the results belong to this realm
		// even if the organisation list changes under the section.
		const realmId = activeRealmId;
		const results = [];
		setImporting(true);
		// A new run starts clean of the last one's success feedback ("Imported just now" and the pill),
		// and the options being re-run lose their last result: an old "Import failed" next to
		// "Importing…" would describe the attempt that is being replaced.
		setOutcomes(current =>
			current.filter(
				outcome =>
					outcome.status !== 'SUCCESS' &&
					(outcome.realmId !== realmId || !chosen.some(type => type.key === outcome.key))
			)
		);
		try {
			// One at a time, in registry order: each call acquires the realm's token, and products
			// link to the accounts imported just before them (spec 032, The run).
			for (const configType of chosen) {
				let response = null;
				try {
					response = await DirectApi.Fetch_Raw_With_Init(configType.importPath, {
						method: 'POST',
						credentials: 'include',
						headers: {'Content-Type': 'application/json'},
						body: JSON.stringify({realmId}),
					});
				} catch (e) {
					response = null;
				}
				results.push({realmId, key: configType.key, ...(await readImportOutcome(response))});
			}
			await loadStatus();
		} finally {
			runningRef.current = false;
			if (mountedRef.current) {
				const ranKeys = chosen.map(type => type.key);
				const succeededKeys = results.filter(result => result.status === 'SUCCESS').map(result => result.key);
				setOutcomes(current => [
					...current.filter(outcome => outcome.realmId !== realmId || !ranKeys.includes(outcome.key)),
					...results,
				]);
				// Failed options stay ticked for a retry (AC9); succeeded ones are done.
				setSelectedKeys(ranKeys.filter(key => !succeededKeys.includes(key)));
				setImporting(false);
			}
		}
	};

	return (
		<ConfigPageCard className="qbo-config-page-card" data-cy="qbo-config-import-section">
			<Heading size="3" as="h2">
				{formatMessage({id: 'settings.app_catalog.quickbooks.config_import_title'})}
			</Heading>
			<ConfigImportDescription>
				<Text size="2">{formatMessage({id: 'settings.app_catalog.quickbooks.config_import_description'})}</Text>
			</ConfigImportDescription>

			<ConfigImportOrganisationField>
				<SubHeading size="2" id={ORGANISATION_LABEL_ID}>
					{formatMessage({id: 'common.organization'})}
				</SubHeading>
				{multiTenant && organisationList.length > 1 ? (
					// Locked while a run is in flight: the results belong to the realm chosen at submit time.
					// A null key is ignored, so the selection can never be emptied (as on Xero).
					<ComboBox
						aria-labelledby={ORGANISATION_LABEL_ID}
						defaultItems={organisationList.map(org => ({id: org.realmId, name: organisationLabel(org)}))}
						value={activeRealmId}
						onChange={key => key && chooseOrganisation(key)}
						disabled={importing}
						// disabled only styles the input; isDisabled also locks the list's trigger button.
						isDisabled={importing}
						width={ComboBox.WIDTH.FLEX}
						data-cy="qbo-config-organization"
					>
						{item => (
							<ComboBox.Item key={item.id} textValue={item.name}>
								<Text>{item.name}</Text>
							</ComboBox.Item>
						)}
					</ComboBox>
				) : (
					// One organisation: its name as plain text, no dropdown (spec 030 AC2).
					<Text size="2" data-cy="qbo-config-organization-name">
						{organisationLabel(activeOrganisation)}
					</Text>
				)}
			</ConfigImportOrganisationField>

			<ConfigImportRows>
				{CONFIG_TYPES.map(configType => {
					const status = describe(configType);
					const message = messageFor(configType);
					const label = formatMessage({id: configType.labelId});
					return (
						<ConfigImportOption key={configType.key} data-config-option={configType.key}>
							<ConfigImportRow>
								{/* aria-label overrides the component's built-in "Select row" name. */}
								<Checkbox
									checked={selectedKeys.includes(configType.key)}
									onChange={() => toggle(configType.key)}
									labelText={label}
									aria-label={label}
									aria-describedby={statusIdFor(configType)}
									notBoldLabel
									disabled={controlsDisabled}
									data-cy={`qbo-config-${configType.key}`}
								/>
								<ConfigImportStatusText
									size="1"
									color="medium"
									noWrap
									id={statusIdFor(configType)}
									data-tone={status.tone}
									aria-live="polite"
								>
									{status.text}
								</ConfigImportStatusText>
							</ConfigImportRow>
							{message ? <ConfigImportMessage aria-live="polite">{message}</ConfigImportMessage> : null}
						</ConfigImportOption>
					);
				})}
			</ConfigImportRows>

			<ConfigImportActions>
				{reconnectRequired ? (
					<Text size="2">{formatMessage({id: 'settings.app_catalog.quickbooks.config_reconnect_required'})}</Text>
				) : null}
				<ConfigImportActionsEnd>
					{showImportedPill ? (
						<Pill variant={PillVariant.SUCCESS}>
							{formatMessage({id: 'settings.app_catalog.quickbooks.config_imported_pill'})}
						</Pill>
					) : null}
					<Button
						emphasis={EMPHASIS.HIGH}
						noMargin
						disabled={controlsDisabled || selectedKeys.length === 0}
						onPress={runImport}
						data-cy="qbo-config-import"
					>
						{formatMessage({
							id: importing
								? 'settings.app_catalog.quickbooks.config_importing'
								: 'settings.app_catalog.quickbooks.config_import_button',
						})}
					</Button>
				</ConfigImportActionsEnd>
			</ConfigImportActions>
		</ConfigPageCard>
	);
};

export default QuickbooksConfigurationImport;
